import * as Headless from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import type React from 'react';

// Shared with the date and team member pickers, whose panels hold more than a list of options.
export const FILTER_PICKER_TRIGGER_CLASS = clsx(
  'inline-flex items-center gap-2 rounded-full border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 px-3 py-1.5',
  'text-xs font-medium text-neutral-700 dark:text-neutral-300 shadow-sm',
  'hover:border-neutral-300 dark:hover:border-white/20 hover:bg-neutral-50 dark:hover:bg-white/5',
  'focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
  'data-[active]:border-primary-300 dark:data-[active]:border-primary-500/30 data-[active]:bg-primary-50 dark:data-[active]:bg-primary-500/10 data-[active]:text-primary-900 dark:data-[active]:text-primary-200'
);

export const FILTER_PICKER_PANEL_CLASS = clsx(
  'z-20 mt-1 w-72 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-2 shadow-lg ring-1 ring-black/5 dark:ring-white/10',
  'origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[enter]:ease-out data-[leave]:duration-75 data-[leave]:ease-in'
);

const OPTION_CLASS = clsx(
  'flex w-full cursor-default items-center justify-between gap-2 rounded-md px-2 py-1.5 text-sm text-neutral-700 dark:text-neutral-300',
  'data-[focus]:bg-neutral-50 dark:data-[focus]:bg-white/5 data-[focus]:outline data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-primary-500',
  'data-[selected]:bg-primary-50 dark:data-[selected]:bg-primary-500/10 data-[selected]:font-medium data-[selected]:text-primary-900 dark:data-[selected]:text-primary-200'
);

export type AnalyticsFilterPickerOption = {
  key: string;
  label: string;
  onSelect: () => void;
};

/**
 * A filter pill that opens a list to pick one option from. data-filter-trigger names the filter,
 * so removing its chip can hand focus back to it.
 */
export const AnalyticsFilterPicker = ({
  icon,
  label,
  heading,
  isActive,
  options,
  selectedKey
}: {
  icon: React.ReactNode;
  label: string;
  heading: string;
  isActive: boolean;
  options: AnalyticsFilterPickerOption[];
  selectedKey: string;
}) => (
  <Headless.Listbox
    value={selectedKey}
    onChange={(key: string) => options.find((option) => option.key === key)?.onSelect()}
  >
    <Headless.ListboxButton
      className={FILTER_PICKER_TRIGGER_CLASS}
      data-active={isActive ? '' : undefined}
      data-filter-trigger={heading}
      title={label}
    >
      {icon}
      <span className="max-w-48 truncate">{label}</span>
      <ChevronDownIcon className="size-3.5 text-neutral-400 dark:text-neutral-500" />
    </Headless.ListboxButton>
    <Headless.ListboxOptions
      transition
      anchor="bottom start"
      aria-label={heading}
      className={clsx(FILTER_PICKER_PANEL_CLASS, 'max-h-80 overflow-y-auto focus:outline-none')}
    >
      <div
        aria-hidden
        className="px-2 py-1 text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
      >
        {heading}
      </div>
      {options.map((option) => (
        <Headless.ListboxOption key={option.key} value={option.key} className={OPTION_CLASS}>
          {({ selected }) => (
            <>
              <span className="truncate">{option.label}</span>
              {selected && (
                <CheckIcon className="size-4 shrink-0 text-primary-600 dark:text-primary-400" />
              )}
            </>
          )}
        </Headless.ListboxOption>
      ))}
    </Headless.ListboxOptions>
  </Headless.Listbox>
);
